'use client'

import { FC, useRef, useMemo, useEffect } from 'react';
import Map, { Marker, MapRef } from 'react-map-gl/mapbox';
import { MapPin, Navigation } from 'lucide-react';
import { MAPBOX_ACCESS_TOKEN } from '../lib/map/token';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Stop } from "@city-sage/core-client";

interface MiniDayMapPreviewProps {
  stops: Stop[];
  dayNumber: number;
}

const MiniDayMapPreview: FC<MiniDayMapPreviewProps> = ({ 
  stops, 
  dayNumber 
}) => {
  const mapRef = useRef<MapRef | null>(null);

  // Get valid stops with coordinates
  const validStops = useMemo(() => {
    return stops.filter(stop => 
      stop.place.coordinates?.latitude && 
      stop.place.coordinates?.longitude &&
      !isNaN(stop.place.coordinates.latitude) &&
      !isNaN(stop.place.coordinates.longitude)
    );
  }, [stops]);

  // Calculate bounds to fit all markers
  const bounds = useMemo(() => {
    if (validStops.length === 0) return null;
    
    const lngs = validStops.map(stop => stop.place.coordinates?.longitude).filter(Boolean);
    const lats = validStops.map(stop => stop.place.coordinates?.latitude).filter(Boolean);
    
    if (lngs.length === 0 || lats.length === 0) return null;
    
    return {
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs),
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats)
    };
  }, [validStops]);

  // Calculate initial view state (fallback for when map first loads)
  const initialViewState = useMemo(() => {
    if (validStops.length === 0) {
      // No coordinates - default view
      return {
        longitude: -98.5795, // Center of US as default
        latitude: 39.8283,
        zoom: 4
      };
    } else if (validStops.length === 1) {
      // Single point - center on it with good zoom
      return {
        longitude: validStops[0].place.coordinates?.longitude || -98.5795,
        latitude: validStops[0].place.coordinates?.latitude || 39.8283,
        zoom: 14
      };
    } else if (bounds) {
      // Multiple points - center on bounds with moderate zoom initially
      const centerLng = (bounds.minLng + bounds.maxLng) / 2;
      const centerLat = (bounds.minLat + bounds.maxLat) / 2;
      
      return {
        longitude: centerLng,
        latitude: centerLat,
        zoom: 10 // This will be overridden by fitBounds
      };
    }
    
    return {
      longitude: -98.5795,
      latitude: 39.8283,
      zoom: 4
    };
  }, [validStops, bounds]);

  // Effect to fit bounds when map loads or stops change
  useEffect(() => {
    if (!mapRef.current || !bounds || validStops.length <= 1) return;

    const map = mapRef.current.getMap();
    
    // Wait for map to be fully loaded
    if (!map.loaded()) {
      map.on('load', () => {
        fitMapToBounds();
      });
    } else {
      fitMapToBounds();
    }

    function fitMapToBounds() {
      if (!bounds) return;
      
      // Use fitBounds with proper padding to ensure all stops are visible
      map.fitBounds(
        [
          [bounds.minLng, bounds.minLat], // Southwest corner
          [bounds.maxLng, bounds.maxLat]  // Northeast corner
        ],
        {
          padding: {
            top: 40,
            bottom: 40,
            left: 40,
            right: 40
          },
          maxZoom: 16, // Don't zoom in too close
          duration: 1000 // Smooth animation
        }
      );
    }
  }, [bounds, validStops.length]);

  // Don't render if no stops
  if (validStops.length === 0) {
    return (
      <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center text-gray-500">
          <MapPin className="h-8 w-8 mx-auto mb-2" />
          <p className="text-sm">No stops with locations yet</p>
          <p className="text-xs">Add stops to see them on the map</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      
      <div className="aspect-video rounded-lg overflow-hidden border border-gray-200 relative">
        <Map
          ref={mapRef}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          initialViewState={initialViewState}
          interactive={true}
        >
          {/* Stop markers */}
          {validStops.map((stop, index) => (
            <Marker
              key={stop.id}
              longitude={stop.place.coordinates?.longitude || 0}
              latitude={stop.place.coordinates?.latitude || 0}
              anchor="center"
            >
              <div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium shadow-lg border-2 border-white">
                {index + 1}
              </div>
            </Marker>
          ))}
        </Map>
      </div>

      {/* Stop count summary */}
      <div className="flex items-center gap-1 text-xs text-gray-500">
        <Navigation className="h-3 w-3" />
        <span>{validStops.length} stops on map</span>
      </div>
    </div>
  );
};

export default MiniDayMapPreview; 